// FRU-23389 (Ed, 2026-09-29 2:09 to 2:14 AM): on the calculator landings, test a "Get Rates & Payments" action with a
// real payment the visitor unlocks. His direction: get the visitor to the loan officers for real payments and options as
// fast as possible; do a real calculation as they progress; keep it locked until the last step; and let Hope tell them
// the payment on the Match page along with the CPC offers and their matches.
//
// So, for half the Hope Direct visitors on a calculator landing (by visitor id), on a refinance, cash-out or purchase:
//  - the calculator display shows "Monthly payment" locked, counting the numbers it uses as they come in;
//  - the email question says loan officers will send their real payments and rate options;
//  - the final button reads "Get Rates & Payments";
//  - at submit, the loan amount goes to this tab's sessionStorage (projects/services/payment-calc) for the Match page,
//    which unlocks the payment with the reviewed 30-year fixed pair.
// Home equity, HELOC and home improvement are second loans with other rates, so they keep today's flow in both versions.
//
// Off switch: HOPE_CALC_PAYMENTS_ENABLED. It stayed off until the Match page could unlock the payment (www-ng #1430), because a
// lock that never opens is a broken promise; switched on 2026-09-29 after the combined landing-to-Match contract and real-iPhone
// walks passed. For testing, calc_payments=1 forces the payments version on a calculator landing and calc_payments=0 forces
// today's, whether the switch is on or off. Matomo experiment FRU-23389-HopeCalcPayments is id 501.
import {
  HOPE_PAYMENT_STORAGE_KEY,
  HopePaymentBasis,
  HopePaymentPurpose,
  HopePaymentRecord,
  hopePaymentPricing,
} from '../../../../services/payment-calc';

export const HOPE_CALC_PAYMENTS_ENABLED = true;

/** Bump to reshuffle who gets which version (a new test). */
export const CALC_PAYMENTS_SALT = 'calc-payments-v1';
export const CALC_PAYMENTS_SEEN_STORAGE_KEY = 'hope_calc_payments_seen_v1';

export type CalcPaymentsArm = 'payments' | 'control';

export const CALC_PAYMENTS_BUTTON_LABEL = 'Get Rates & Payments';
export const CALC_PAYMENTS_BUTTON_LABEL_ES = 'Ver tasas y pagos';

/** 0 to 99, the same for a given visitor id every time (FNV-1a over the id and the salt). */
export function calcPaymentsBucket(visitorId: string, salt: string = CALC_PAYMENTS_SALT): number {
  const input = (visitorId || '') + '|' + salt;
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash % 100;
}

/**
 * Which version this chat gets, decided once when it starts. null means not in the test: the chat isn't wearing the
 * calculator skin (so not Hope Direct on a calculator landing), no visitor id (tracking opted out), or the switch is off
 * with no override.
 */
export function calcPaymentsArmFor(opts: {
  calcSkin: boolean;
  search: string;
  visitorId: string;
  enabled?: boolean;
}): CalcPaymentsArm | null {
  if (!opts.calcSkin) { return null; }
  let override: string | null = null;
  try { override = new URLSearchParams(opts.search || '').get('calc_payments'); } catch (e) { return null; }
  if (override === '1') { return 'payments'; }
  if (override === '0') { return 'control'; }
  const enabled = opts.enabled === undefined ? HOPE_CALC_PAYMENTS_ENABLED : opts.enabled;
  if (!enabled || !opts.visitorId) { return null; }
  return calcPaymentsBucket(opts.visitorId) < 50 ? 'payments' : 'control';
}

/** The loan type the visitor picked, as a payment purpose, or null for a second loan (no payment is promised). */
export function calcPaymentsPurpose(lpButton: any): HopePaymentPurpose | null {
  const v = (lpButton == null ? '' : String(lpButton)).toLowerCase().trim();
  if (v === 'refinance') { return 'refinance'; }
  if (v === 'cashout') { return 'cash_out'; }
  if (v === 'purchase') { return 'purchase'; }
  return null;
}

function amount(v: any): number | null {
  if (v === null || v === undefined || v === '') { return null; }
  const n = Number(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** The form answers the payment reads. Values are raw form control values. */
export interface CalcPaymentsAnswers {
  newHomeValue?: any;
  estimatedDownPayment?: any;   // a percent (the slider offers 0 to 95)
  propertyValue?: any;
  firstMortgageBalance?: any;
  maximumEquity?: any;          // 'yes' = the most cash available
  cashOut?: any;
  cashOutAction?: any;          // '1' once the visitor set the amount
  currentVA?: any;              // 'yes' on a current VA loan: the maximum is 100% of the home's value, not 90%
}

/** Reads the answers the payment uses from the chat's form (any object with get(key).value). */
export function calcPaymentsAnswersFrom(form: any): CalcPaymentsAnswers {
  const v = (k: string) => { try { const c = form && form.get(k); return c ? c.value : undefined; } catch (e) { return undefined; } };
  return {
    newHomeValue: v('newHomeValue'), estimatedDownPayment: v('estimatedDownPayment'), propertyValue: v('propertyValue'),
    firstMortgageBalance: v('firstMortgageBalance'), maximumEquity: v('maximumEquity'), cashOut: v('cashOut'),
    cashOutAction: v('cashOutAction'), currentVA: v('currentVA'),
  };
}

/** The numbers the payment uses for this purpose, and how many of them the visitor has given. */
export function calcPaymentsProgress(purpose: HopePaymentPurpose, a: CalcPaymentsAnswers): { have: number; need: number } {
  if (purpose === 'purchase') {
    const have = (amount(a.newHomeValue) ? 1 : 0) + (amount(a.estimatedDownPayment) !== null ? 1 : 0);
    return { have, need: 2 };
  }
  // Refinance and cash-out: the balance always; with the maximum cash, the home value too; otherwise the cash amount when
  // the visitor gave one (a cash-out always asks for it, so there it counts even at zero).
  const max = String(a.maximumEquity || '').toLowerCase() === 'yes';
  const balanceIn = amount(a.firstMortgageBalance) !== null;
  if (max) {
    return { have: [balanceIn, !!amount(a.propertyValue)].filter(Boolean).length, need: 2 };
  }
  const cash = amount(a.cashOut);
  const cashIn = String(a.cashOutAction || '') === '1' && cash !== null;
  const needsCash = purpose === 'cash_out' || (cashIn && !!cash);
  return { have: [balanceIn, needsCash && cashIn].filter(Boolean).length, need: needsCash ? 2 : 1 };
}

/**
 * The loan the payment is for, worked out from the visitor's own answers exactly as the lead Hope submits works it out,
 * or null when there isn't one yet (or it would be zero). Refinance and cash-out share one rule; only their rates differ.
 * "The maximum" is what ChatComponent's submit computes for maximumEquity yes: cash = home value x 90% (100% on a current
 * VA loan) minus the balance, so the loan is 90% (or 100%) of the home's value. When that leaves no cash, it's the balance.
 */
export function calcPaymentsLoan(purpose: HopePaymentPurpose, a: CalcPaymentsAnswers): { loanAmount: number; basis: HopePaymentBasis } | null {
  if (purpose === 'purchase') {
    const price = amount(a.newHomeValue);
    const down = amount(a.estimatedDownPayment);
    if (!price || down === null || down >= 100) { return null; }
    const loan = Math.round(price * (1 - down / 100));
    return loan > 0 ? { loanAmount: loan, basis: 'price_minus_down' } : null;
  }
  const balance = amount(a.firstMortgageBalance);
  if (balance === null) { return null; }
  if (String(a.maximumEquity || '').toLowerCase() === 'yes') {
    const value = amount(a.propertyValue);
    if (!value) { return null; }
    const share = String(a.currentVA || '').toLowerCase() === 'yes' ? 1 : 0.9;
    const cash = value * share - balance;
    if (cash > 0) { return { loanAmount: Math.round(balance + cash), basis: 'max_cash' }; }
    return balance > 0 ? { loanAmount: Math.round(balance), basis: 'balance' } : null;
  }
  const cash = String(a.cashOutAction || '') === '1' ? (amount(a.cashOut) || 0) : 0;
  if (cash > 0) { return { loanAmount: Math.round(balance + cash), basis: 'balance_plus_cash' }; }
  return balance > 0 ? { loanAmount: Math.round(balance), basis: 'balance' } : null;
}

/**
 * The locked readout on the calculator display. "Calculated" only once the payment really has been (every number in and
 * the reviewed 30-year pair in hand); with the numbers in but no pair it says it's ready, never calculated.
 */
export function calcPaymentsLockDisplay(purpose: HopePaymentPurpose, a: CalcPaymentsAnswers, priced = false, spanish = false): {
  label: string; sub: string; locked: true;
} {
  const { have, need } = calcPaymentsProgress(purpose, a);
  const done = have >= need;
  if (spanish) {
    return { label: 'Pago mensual', locked: true,
      sub: done ? (priced ? 'Calculado · se desbloquea con sus tasas' : 'Listo · se desbloquea con sus tasas') : `Calculando · ${have} de ${need} datos` };
  }
  return { label: 'Monthly payment', locked: true,
    sub: done ? (priced ? 'Calculated · unlocks with your rates' : 'Ready · unlocks with your rates') : `Calculating · ${have} of ${need} numbers in` };
}

/** The visitor's payment priced on the reviewed pair Hope holds for this purpose, or null. */
export function calcPaymentsPriced(purpose: HopePaymentPurpose | null, a: CalcPaymentsAnswers, pair: any) {
  if (!purpose || !pair || pair.status !== 'ok' || pair.purpose !== purpose || pair.product !== '30yr_fixed') { return null; }
  const loan = calcPaymentsLoan(purpose, a);
  return loan ? hopePaymentPricing(loan.loanAmount, pair) : null;
}

/**
 * The payments version's email question. It promises only what ships: the payment Hope calculated unlocks at the end (the
 * Match page), said only when there is a loan to price. The review pass caught an earlier "loan officers email your real
 * payments", which nothing delivers. It is as short as today's question ("Almost there! What's your email? We take privacy
 * seriously, no spam."): a line longer put Continue under the keyboard on a real Galaxy S24 (FRU-23777). The lift, when
 * there is one, already ends ready for a lowercase word.
 */
export function calcPaymentsEmailQuestion(lift: string, paymentReady = true): string {
  const q = "what's your email?"
    + (paymentReady ? ' Your calculated payment unlocks at the end. No spam.' : ' We take privacy seriously, no spam.');
  return lift ? lift + q : q.charAt(0).toUpperCase() + q.slice(1);
}

/** The record the Match page unlocks, or null when there's nothing honest to promise. */
// One invariant everywhere (abx, 9/29): the payments version promises, labels or stores a payment only when it priced
// one, i.e. calcPaymentsPriced() returned a value. A slow or failed rate therefore means no record at all, rather than an
// unpriced one the Match page would have to price itself.
export function calcPaymentsRecord(purpose: HopePaymentPurpose | null, a: CalcPaymentsAnswers, now: number, pair: any = null): HopePaymentRecord | null {
  if (!purpose) { return null; }
  const loan = calcPaymentsLoan(purpose, a);
  const priced = calcPaymentsPriced(purpose, a, pair);
  if (!loan || !priced) { return null; }
  return { v: 1, arm: 'payments', purpose, product: '30yr_fixed', loanAmount: loan.loanAmount, basis: loan.basis, at: now, priced };
}

/** The pending record, stamped with the lead id the submit returned (the one the thank-you URL carries), or null. */
export function calcPaymentsRecordForLead(pending: HopePaymentRecord | null, leadId: any): HopePaymentRecord | null {
  if (!pending || leadId === null || leadId === undefined || String(leadId).trim() === '') { return null; }
  return { ...pending, lead: String(leadId).trim() };
}

export function writeCalcPaymentsRecord(rec: HopePaymentRecord | null): void {
  try {
    if (typeof sessionStorage === 'undefined') { return; }
    if (rec) { sessionStorage.setItem(HOPE_PAYMENT_STORAGE_KEY, JSON.stringify(rec)); }
    else { sessionStorage.removeItem(HOPE_PAYMENT_STORAGE_KEY); }
  } catch (e) { /* storage blocked: the Match page falls back to "your loan officers will give your real payment" */ }
}

/** The Matomo A/B experiment this test reads in: goal 27 by variation. */
export const CALC_PAYMENTS_EXPERIMENT = 'FRU-23389-HopeCalcPayments';
export function calcPaymentsVariationName(arm: CalcPaymentsArm): string {
  return arm === 'payments' ? 'RatesAndPayments' : 'original';
}

/** Reload-safe proof that this exact visitor saw this exact version in this tab. */
export function calcPaymentsEnrollmentMarker(visitorId: string, arm: CalcPaymentsArm | null): string {
  if (!visitorId || !arm) { return ''; }
  return [CALC_PAYMENTS_SALT, visitorId, arm].join('|');
}

/** The lead's experiment row, for a visitor who actually saw the first screen of either version. */
export function calcPaymentsLeadExperiment(
  arm: CalcPaymentsArm | null,
  screenLogged: boolean,
  experimentEligible: boolean,
): { experiment: string; variation: string; hit_segment: boolean } | null {
  if (!arm || !screenLogged || !experimentEligible) { return null; }
  return { experiment: CALC_PAYMENTS_EXPERIMENT, variation: calcPaymentsVariationName(arm), hit_segment: true };
}

/**
 * Enters the visitor into the Matomo experiment with the version this code assigned. A visit that forced a version with
 * calc_payments= is a test, and is left out. Never throws.
 */
export function reportCalcPaymentsVariation(arm: CalcPaymentsArm, search: string): void {
  const w: any = typeof window !== 'undefined' ? window : null;
  if (!w) { return; }
  try { if (new URLSearchParams(search || '').has('calc_payments')) { return; } } catch (e) { return; }
  const variation = calcPaymentsVariationName(arm);
  const enter = () => {
    try {
      const exp = new w.Matomo.AbTesting.Experiment({
        name: CALC_PAYMENTS_EXPERIMENT,
        variations: ['original', 'RatesAndPayments'].map(name => ({ name, activate: () => { /* Hope renders it itself */ } })),
        trigger: () => false,
      });
      exp.forceVariation(variation);
    } catch (e) { /* non-fatal */ }
  };
  try {
    if (typeof w.Piwik === 'object' && w.Matomo && typeof w.Matomo.AbTesting === 'object') { enter(); return; }
    const prev = w.matomoAbTestingAsyncInit;
    w.matomoAbTestingAsyncInit = function () { if (typeof prev === 'function') { try { prev(); } catch (e) { /* keep going */ } } enter(); };
  } catch (e) { /* non-fatal */ }
}
